import { useState, useMemo, useCallback } from "react";
import { GridFilterModel } from "@mui/x-data-grid";
import { ITag } from "../types/Tag";
import { useGetTeamTags } from "../queries/useTags";

interface FilterState {
  searchQuery: string;
  tags: number[];
  dateLowerBound?: string;
  dateUpperBound?: string;
  dateBefore?: string;
  dateAfter?: string;
  teamMembers?: number[];
  phone?: string;
}

interface UseContactsFilterProps {
  teamId: number;
}

type FilterAccumulator = {
  infoFilter?: string;
  userFilter?: string;
  tagsFilter?: string | string[];
  dateFilter?: Date[];
  dateBeforeFilter?: Date;
  dateAfterFilter?: Date;
  phoneFilter?: string;
};

type FilterHandler = (value: any, acc: FilterAccumulator, item: any) => void;

const filterHandlers: Record<string, FilterHandler> = {
  info: (value: string, acc: FilterAccumulator) => {
    acc.infoFilter = value;
  },
  user: (value: string, acc: FilterAccumulator) => {
    acc.userFilter = value;
  },
  tags: (value: string | string[], acc: FilterAccumulator) => {
    acc.tagsFilter = value;
  },
  created: (
    value: Date[] | Date | null | undefined,
    acc: FilterAccumulator,
    item: any,
  ) => {
    if (!value) return;

    if (item.operator === "inBetween") {
      acc.dateFilter = Array.isArray(value) ? value : [value];
    } else if (item.operator === "before") {
      acc.dateBeforeFilter = Array.isArray(value) ? value[0] : value;
    } else if (item.operator === "after") {
      acc.dateAfterFilter = Array.isArray(value) ? value[0] : value;
    }
  },
  phone: (value: string, acc: FilterAccumulator) => {
    acc.phoneFilter = value;
  },
};

const extractFiltersFromModel = (
  filterModel: GridFilterModel,
  teamTags?: ITag[],
): FilterState => {
  const quickFilter = filterModel.quickFilterValues?.[0] || "";

  // Single pass through items to find all filters
  const filters = filterModel.items.reduce((acc, item) => {
    const handler = filterHandlers[item.field];
    if (handler) {
      handler(item.value, acc, item);
    }
    return acc;
  }, {} as FilterAccumulator);

  const searchQuery = filters.infoFilter || quickFilter;
  const tagNames = filters.tagsFilter
    ? Array.isArray(filters.tagsFilter)
      ? filters.tagsFilter
      : [filters.tagsFilter]
    : [];
  const tags = teamTags
    ? tagNames
        .map(name => teamTags.find(tag => tag.name === name)?.id)
        .filter((id): id is number => id !== undefined)
    : [];
  const dateRange =
    filters.dateFilter?.map(date => date?.toISOString()).filter(Boolean) || [];
  const [dateLowerBound, dateUpperBound] = dateRange;

  const beforeDate = filters.dateBeforeFilter
    ? filters.dateBeforeFilter.toISOString()
    : undefined;
  const afterDate = filters.dateAfterFilter
    ? filters.dateAfterFilter.toISOString()
    : undefined;

  const teamMembers = filters.userFilter ? [Number(filters.userFilter)] : [];
  return {
    searchQuery,
    tags,
    dateLowerBound,
    dateUpperBound,
    dateBefore: beforeDate,
    dateAfter: afterDate,
    teamMembers,
    phone: filters.phoneFilter,
  };
};

export const useContactsFilter = ({ teamId }: UseContactsFilterProps) => {
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterExcludeHiddenColumns: true,
  });

  const { data: teamTagsData } = useGetTeamTags({
    teamId,
  });

  const teamTags = teamTagsData?.results || [];

  const serverFilterModel = useMemo(() => {
    return extractFiltersFromModel(filterModel, teamTags);
  }, [filterModel, teamTags]);

  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      setFilterModel(newFilterModel);
    },
    [],
  );

  return {
    filterModel,
    handleFilterModelChange,
    serverFilterModel,
  };
};
